import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { map, filter, reduce, cloneDeep, first } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import ReactResizeDetector from 'react-resize-detector';
import ProgressBar from '../../../../components/progress-bar';
import SearchField from '../../../../components/search-field';
// Import Actions
import { 
  requestFetchForecastCategoryNameFields, requestUpdateForecastCategoryNameFields,
} from '../../SetupActions';

// Import Selectors
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import CustomHeader from '../../../../components/god-table/header';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';

class ForecastCategoryNamePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      error: null,
      allUpdating: false,
      fields: [],
      dirty: false,
      navBarHeight: 0,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsForecastNameField = this.markAsForecastNameField.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }
  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  loadFields(rawFields) {
    return reduce(rawFields || [], (items, field) => {
      if (field.sync_required === true || field.is_forecast_name_field === true) {
        const f = cloneDeep(field);
        if (f.name === 'ForecastCategoryName') {
          f.is_default = true;
        }
        f.original_is_forecast_name_field = f.is_forecast_name_field;
        items.push(f);
      }
      return items;
    }, []);
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });
    const allFieldsPromise = this.props.dispatch(requestFetchForecastCategoryNameFields());
    allFieldsPromise.then(
      (results) => {
        // clone!!!!! results.fields
        const fields = this.loadFields(results.fields);
        this.setState({
          loading: false, error: null, loaded: true, fields,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsForecastNameField(field) {
    const { fields } = this.state;
    const newFields = fields.map((f) => {
      if (f._id === field._id) {
        return { ...f, is_forecast_name_field: true };
      }
      return { ...f, is_forecast_name_field: false };
    });
    const dirtyFields = filter(newFields, f => f.original_is_forecast_name_field !== f.is_forecast_name_field);
    const dirty = dirtyFields.length > 0;
    this.setState({ dirty, fields: newFields }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true });
    const selectedField = first(map(filter(fields, { is_forecast_name_field: true }), 'name'));
    this.props.dispatch(requestUpdateForecastCategoryNameFields({ forecast_name_field: selectedField }))
      .then(
        (results) => {
          const newFields = this.loadFields(results.fields);
          this.setState({ allUpdating: false, fields: newFields, dirty: false });
        },
        () => {
          this.setState({ allUpdating: false });
        },
      );
  }

  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      fields, allUpdating, dirty,
      navBarHeight,
    } = this.state;

    if (fields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>picklist</code> found.
        </div>
      );
    }

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 315,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 120,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use as Forecast Category Name',
        field: 'is_forecast_name_field',
        suppressMenu: true,
        width: 250,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsForecastNameField,
          disabled: allUpdating,
          isRadio: true,
        },
      },  
    ];

    const viewableHeight = viewableDimensions.height - navBarHeight;
    return (
      <div>
        {fields.length > 0 && 
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <button onClick={this.submit} disabled={allUpdating} className="btn btn-primary">
                  Save &amp; Update
                </button>
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={fields}
          />
        </div>
      </div>
    );
  }

  renderInstruction(orgType) {
    let opportunityObjectName = 'Opportunity';
    if (orgType === 'hubspot') {
      opportunityObjectName = 'deal';
    }
    return (
      <div className="well well-sm">
        <div>
          <p>
            Select the field TruVoice Automation should use for Forecast Category Name for your sales opportunities.
          </p>
          <p>
            If you cannot find the field(s) you are looking for, you can also <Link to={`/setup/sync-settings/${opportunityObjectName}`}>add more custom fields to the sync</Link>.
          </p>
        </div>
      </div>
    );
  }

  render() {
    const { loading, loaded } = this.state;
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    const orgType = organization && organization.type;
    return (
      <div>
        <Helmet title="Manage Forecast Category Name Integration" />
        <div className="container">
          {!isDemo && this.renderInstruction(orgType)}
          {loading && <ProgressBar />}
          {!loading && loaded &&
            <div>
              {!isDemo && this.renderTable()}
              {isDemo &&
                <div className="alert alert-warning">
                  This is a demo organization, please switch back to the original organization to change the forecast category name field mapping.
                </div>
              }
            </div>
          }
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
  };
}

ForecastCategoryNamePage.propTypes = {
  organization: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
};

ForecastCategoryNamePage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(ForecastCategoryNamePage);
